import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { userService } from '../../../../services/user.service';
import RegisterStudentForm from "../../../auth/RegisterStudentForm"

const CreateUserModal = ({ onClose, onSuccess, creatorRole }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isStudentForm, setIsStudentForm] = useState(false);
  const [staffFormData, setStaffFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phone: '',
    role: creatorRole === 'receptionist' ? 'counselor' : 'counselor',
    canDoVCT: false,
  });
  
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    if (creatorRole === 'admin') {
      setRoleOptions(['counselor', 'admin', 'receptionist', 'hod', 'student','staff']);
    } else if (creatorRole === 'receptionist') {
      setRoleOptions(['student', 'counselor']);
    }
  }, [creatorRole]);

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setIsStudentForm(newRole === 'student');
    if (!isStudentForm) {
      setStaffFormData(prev => ({ ...prev, role: newRole }));
    }
  };

  const handleVCTChange = (e) => {
    setStaffFormData(prev => ({ ...prev, canDoVCT: e.target.checked }));
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await userService.createUser(staffFormData);
      if (result && result.status === 'success') {
        onSuccess(result.data.user);
        onClose();
      } else {
        throw new Error('Invalid server response');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`bg-white rounded-lg w-full ${isStudentForm ? 'max-w-2xl' : 'max-w-md'} p-6 max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Create New User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            User Type <span className="text-red-500">*</span>
          </label>
          <select
            value={isStudentForm ? 'student' : staffFormData.role}
            onChange={handleRoleChange}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
            required
          >
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {isStudentForm ? (
          <RegisterStudentForm 
            inModal={true} 
            onSuccess={(userData) => {
              onSuccess(userData);
              onClose();
            }}
            onCancel={onClose}
          />
        ) : (
          <form onSubmit={handleStaffSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={staffFormData.fullName}
                onChange={(e) => setStaffFormData(prev => ({
                  ...prev,
                 fullName: e.target.value
                }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={staffFormData.email}
                  onChange={(e) => setStaffFormData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={staffFormData.username}
                  onChange={(e) => setStaffFormData(prev => ({
                    ...prev,
                    username: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={staffFormData.password}
                  onChange={(e) => setStaffFormData(prev => ({
                    ...prev,
                    password: e.target.value
                  }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
                  required
                  minLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 6 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={staffFormData.phone}
                  onChange={(e) => setStaffFormData(prev => ({
                    ...prev,
                     phone: e.target.value 
                  }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* VCT Checkbox - only show for counselors */}
            {!isStudentForm && (staffFormData.role === 'counselor' || staffFormData.role === 'hod') && (
              <div className="mt-4">
                <div className="flex items-center">
                  <input
                    id="vct-checkbox"
                    type="checkbox"
                    checked={staffFormData.canDoVCT}
                    onChange={handleVCTChange}
                    className="h-4 w-4 text-uzima-maroon-500 focus:ring-uzima-maroon-400 border-gray-300 rounded"
                  />
                  <label htmlFor="vct-checkbox" className="ml-2 block text-sm text-gray-700">
                    Can perform VCT counseling
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Check this box if this counselor is qualified to provide VCT services
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md border border-gray-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateUserModal;